import React, { useState, useEffect } from 'react';
import { useSpring, animated } from 'react-spring';
import styled from 'styled-components';
import { above, size } from '../../../style/util';
import { Times } from '../../common/Icons/Light';
import { common } from '../../../style';
import { neutral } from '../../../style/colors';
import { useMedia } from '../../../hooks/useMedia';
import { ILokalplan } from '../../../Interfaces/ILokalplan';
import SearchResult from './SearchResult';
import SearchResultLoading from './SearchResultLoading';
import Spinner from '../../Spinner';
import SeptimaSearch from './SeptimaSearch/SeptimaSearch';
import { ISeptimaSearchResultItem } from './SeptimaSearch/interfaces';

// TODO: Handle search result were geometry (adgangsadresse) is not found, e.g. Asagården 1, st. tv, 7500 Holstebro

const SearchSidebar = React.memo((props: ISearchPropTypes) => {
  const { onClose, toggled, lokalPlaner, onListItemClick } = props;
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [septimaSearchConfig, setSeptimaSearchConfig] = useState(undefined);
  useEffect(() => {
    getSeptimaSearchConfig()
      .then(resp => resp.text().then(conf => setSeptimaSearchConfig(conf)))
      .catch(err => console.log(err));
  }, []);
  useEffect(() => {
    setLoading(true);
  }, [searchResult]);
  useEffect(() => {
    setLoading(false);
  }, [lokalPlaner]);
  useEffect(() => {
    if (toggled && searchResult) {
      props.onSearchResult(searchResult);
    }
    // eslint-disable-next-line
  }, [toggled]);
  const isSmallScreen = useMedia(
    [`(max-width: ${size.small}px)`],
    [true],
    false
  );
  const navAnimation = useSpring({
    config: { duration: 150 },
    right: !isSmallScreen ? (toggled ? '0px' : '-430px') : '0px',
    bottom: isSmallScreen ? (toggled ? '0vh' : '-40vh') : '0vh',
  });

  const onSearchResult = (result: ISeptimaSearchResultItem) => {
    setSearchResult(result);
    props.onSearchResult(result);
  };
  const renderSearchResultText = () => {
    if (searchResult) {
      return (
        <h2 style={{ marginBottom: 18, alignSelf: 'center' }}>
          {searchResult.title}
        </h2>
      );
    }
    return <h1 style={{ marginBottom: 18 }}>&nbsp;</h1>;
  };
  const keys = new Set();
  const uniqueLokalPlaner = lokalPlaner
    .filter(plan => {
      const { planid } = plan.feature.properties;
      if (keys.has(planid)) {
        return false;
      }
      keys.add(planid);
      return true;
    })
    .slice(0, 10);
  const renderLoadingBoxes = () => {
    return Array.from(new Array(9)).map((a, i) => {
      return <SearchResultLoading key={i} />;
    });
  };
  const renderLokalPlaner = () => {
    if (loading)
      return (
        <>
          <SpinnerWrapper>
            <Spinner />
            Finder nærmeste planer ...
          </SpinnerWrapper>
          {renderLoadingBoxes()}
        </>
      );
    return uniqueLokalPlaner.map(lokalPlan => {
      return (
        <SearchResult
          onClick={onListItemClick}
          key={lokalPlan.feature.properties.planid}
          lokalplan={lokalPlan}
        />
      );
    });
  };

  const wrapperStyle = {
    position: 'fixed' as any,
    bottom: 0,
    left: isSmallScreen ? 0 : 'unset',
    backgroundColor: `${neutral[200]}`,
    zIndex: 4,
    height: isSmallScreen ? '40vh' : '100%',
    width: isSmallScreen ? 'unset' : 430,
    overflowY: isSmallScreen ? ('auto' as any) : ('none' as any),
    display: 'flex',
    flexDirection: 'column' as any,
    boxShadow: toggled ? common.boxShadow : undefined,
  };
  const renderSeptimaSearch = () => {
    if (septimaSearchConfig) {
      return (
        <SeptimaSearch onResult={onSearchResult} config={septimaSearchConfig} />
      );
    }
    return null;
  };
  return (
    <animated.div style={{ ...wrapperStyle, ...navAnimation }}>
      <SearchBoxContainer>
        <div style={{ height: 55 }}>&nbsp;</div>
        <Times
          onClick={onClose}
          color={neutral[600]}
          width={18}
          height={18}
          style={{
            position: 'absolute',
            top: 18,
            right: 18,
            cursor: 'pointer',
          }}
        />
        {renderSeptimaSearch()}
        {renderSearchResultText()}
      </SearchBoxContainer>
      <ScrollWrapper>
        <SearchResultContainer>{renderLokalPlaner()}</SearchResultContainer>
      </ScrollWrapper>
    </animated.div>
  );
});
async function getSeptimaSearchConfig() {
  return await fetch(process.env.PUBLIC_URL + '/config.yml');
}
const SearchBoxContainer = styled.div`
  min-height: 170px;
  box-shadow: ${common.boxShadow};
  background-color: ${common.backgroundColor};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-evenly;
  padding: 0 18px;
  position: relative;
`;
const ScrollWrapper = styled.div`
  overflow-y: none;
  flex: 1;
  ${(above as any).small`
    overflow-y: auto;
  `}
`;

const SearchResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0;
`;
const SpinnerWrapper = styled.div`
  height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  padding: 20px;
`;

interface ISearchPropTypes {
  onClose?: Function;
  toggled: boolean;
  onSearchResult: Function;
  lokalPlaner?: ILokalplan[];
  onListItemClick?: Function;
}

export default SearchSidebar;
